import type { PortProfile } from '@/graphql/generated/graphql'

/**
 * One profile as the form offers it: the enum value to send, a name a visitor
 * can read, and what the sweep would reach.
 */
export interface PortProfileOption {
  readonly value: PortProfile
  readonly label: string
  /**
   * The services behind the profile, named rather than counted.
   *
   * A visitor picking a profile is answering "which of my things do I want
   * looked at", and neither the enum value nor a port count answers that.
   * "DATABASE" does not tell somebody running MongoDB that it is in there.
   */
  readonly reach: string
}

/**
 * The six profiles, keyed by the schema's enum so the set cannot drift.
 *
 * Typing this as a total `Record<PortProfile, …>` is the whole point: codegen
 * writes `PortProfile` from the schema, so a seventh profile appearing there
 * fails `vue-tsc` here until somebody writes its label, rather than quietly
 * going missing from the form. That is also why the labels live in one object
 * instead of being spelled out in the template.
 *
 * The order is the schema's own, narrowest first, ending with the one that is
 * the union of the rest. GraphQL enums carry no ordering and codegen emits the
 * union alphabetically, so the order a visitor reads is a client decision, the
 * same way `CATEGORY_ORDER` in tone.ts is.
 */
const PORT_PROFILE_META: Readonly<Record<PortProfile, Omit<PortProfileOption, 'value'>>> = {
  WEB: {
    label: 'Web servers',
    reach: 'HTTP and HTTPS, including 8080 and 8443',
  },
  MAIL: {
    label: 'Mail servers',
    reach: 'SMTP, submission, IMAP and POP3, with and without TLS',
  },
  REMOTE: {
    label: 'Remote access',
    reach: 'SSH, telnet, RDP, VNC, Windows management and file sharing',
  },
  DATABASE: {
    label: 'Databases and caches',
    reach: 'MSSQL, Oracle, MySQL, Postgres, Redis, Elasticsearch, memcached, MongoDB, RabbitMQ',
  },
  CONTAINER: {
    label: 'Container control planes',
    reach: 'the Docker daemon and the Kubernetes API server',
  },
  COMMON: {
    label: 'Everything above',
    reach: 'all five profiles at once, for an inventory of the machine',
  },
}

/**
 * The profiles in the order the form lists them.
 *
 * `Object.keys` gives back `string[]`, and the assertion below narrows it to
 * what the object's own type already guarantees the keys are. It is the one
 * cast in this file and it buys the exhaustiveness check described above.
 */
export const PORT_PROFILE_OPTIONS: readonly PortProfileOption[] = (
  Object.keys(PORT_PROFILE_META) as PortProfile[]
).map((value) => ({ value, ...PORT_PROFILE_META[value] }))
